/**
 * Browser storage for revocable session tokens.
 * The active token is what API calls send. The roster remembers other
 * person and organization sessions so the profile menu can switch accounts.
 */

const SESSION_TOKEN_KEY = "session_token";
const SESSION_USER_ID_KEY = "session_user_id";
const SESSION_USER_TYPE_KEY = "session_user_type";
const ROSTER_KEY = "session_roster";

/** Saved person and organization sessions on this browser. */
export const MAX_SAVED_ACCOUNTS = 5;

/** Shown when a new login would exceed {@link MAX_SAVED_ACCOUNTS}. */
export const ROSTER_FULL_MESSAGE =
  "You can save up to 5 accounts on this browser. Sign one out first.";

/**
 * @typedef {Object} SavedAccount
 * @property {string} key `user:12` or `organization:44`
 * @property {number} id
 * @property {"user"|"organization"} type
 * @property {string} token
 * @property {string} name
 * @property {string} username
 * @property {string} pfp_url
 * @property {number} lastUsed
 */

/**
 * @typedef {Object} SessionRoster
 * @property {string} activeKey
 * @property {SavedAccount[]} accounts
 */

/**
 * Empty roster.
 * @returns {SessionRoster}
 */
function emptyRoster() {
  return { activeKey: "", accounts: [] };
}

/**
 * Read a localStorage item, or an empty string when storage is blocked.
 * @param {string} key
 * @returns {string}
 */
function readItem(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

/**
 * Write or remove a localStorage item.
 * @param {string} key
 * @param {string} value Empty removes the item
 * @returns {void}
 */
function writeItem(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Storage can be unavailable in a private window.
  }
}

/**
 * @param {string} type
 * @param {number} id
 * @returns {string}
 */
export function accountKey(type, id) {
  return `${type}:${id}`;
}

/**
 * Home route for a signed-in person or organization.
 * @param {string} type
 * @returns {string}
 */
export function accountHomePath(type) {
  return type === "organization" ? "/organization-admin" : "/feed";
}

/**
 * Label stored on a roster row.
 * @param {Object} account
 * @param {"user"|"organization"} type
 * @returns {string}
 */
function accountDisplayName(account, type) {
  if (type === "organization") {
    return String(account?.name || account?.username || "").trim();
  }
  const full = `${account?.first_name || ""} ${account?.last_name || ""}`.trim();
  return full || String(account?.username || account?.name || "").trim();
}

/**
 * @param {Object|null|undefined} account
 * @returns {"user"|"organization"|"admin"|""}
 */
function accountTypeOf(account) {
  if (account?.type === "organization" || account?.type === "user" || account?.type === "admin") {
    return account.type;
  }
  return "";
}

/**
 * Keep only well-formed person and organization rows.
 * @param {Object|null|undefined} parsed
 * @returns {SessionRoster}
 */
function sanitizeRoster(parsed) {
  const accounts = [];
  const seen = new Set();
  const rows = Array.isArray(parsed?.accounts) ? parsed.accounts : [];
  rows.forEach((row) => {
    const type = row?.type === "organization" ? "organization" : row?.type === "user" ? "user" : "";
    const id = Number(row?.id);
    const token = typeof row?.token === "string" ? row.token : "";
    if (!type || !id || !token) return;
    const key = accountKey(type, id);
    if (seen.has(key)) return;
    seen.add(key);
    accounts.push({
      key,
      id,
      type,
      token,
      name: typeof row.name === "string" ? row.name : "",
      username: typeof row.username === "string" ? row.username : "",
      pfp_url: typeof row.pfp_url === "string" ? row.pfp_url : "",
      lastUsed: Number(row.lastUsed) || 0,
    });
  });
  const active = accounts.find((row) => row.key === parsed?.activeKey) || mostRecent(accounts);
  return { activeKey: active?.key || "", accounts };
}

/**
 * @param {SavedAccount[]} accounts
 * @returns {SavedAccount|null}
 */
function mostRecent(accounts) {
  if (!accounts.length) return null;
  return [...accounts].sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0))[0];
}

/**
 * @param {SessionRoster} roster
 * @returns {void}
 */
function writeRoster(roster) {
  try {
    localStorage.setItem(ROSTER_KEY, JSON.stringify(roster));
  } catch {
    // Storage can be unavailable in a private window.
  }
}

/**
 * Point the active bearer token and account id at one saved account.
 * @param {SavedAccount} account
 * @returns {void}
 */
function writeActiveIdentity(account) {
  writeItem(SESSION_TOKEN_KEY, account.token || "");
  writeItem(SESSION_USER_ID_KEY, account.id ? String(account.id) : "");
  writeItem(SESSION_USER_TYPE_KEY, account.type || "");
}

/**
 * Build a roster from the previous single-session keys, when present.
 * Writes an empty roster when there is nothing to migrate so a later admin
 * token cannot be paired with a leftover account id.
 * @returns {SessionRoster}
 */
export function migrateLegacyRoster() {
  try {
    const existing = localStorage.getItem(ROSTER_KEY);
    if (existing != null) return sanitizeRoster(JSON.parse(existing));
  } catch {
    return emptyRoster();
  }

  const token = readItem(SESSION_TOKEN_KEY);
  const id = parseInt(readItem(SESSION_USER_ID_KEY), 10);
  const type = readItem(SESSION_USER_TYPE_KEY);
  if (!token || !id || (type !== "user" && type !== "organization")) {
    const empty = emptyRoster();
    writeRoster(empty);
    return empty;
  }

  const row = {
    key: accountKey(type, id),
    id,
    type,
    token,
    name: "",
    username: "",
    pfp_url: "",
    lastUsed: Date.now(),
  };
  const roster = { activeKey: row.key, accounts: [row] };
  writeRoster(roster);
  return roster;
}

/**
 * Read the saved accounts. Migrates the old single session on first use.
 * @returns {SessionRoster}
 */
export function getRoster() {
  try {
    const raw = localStorage.getItem(ROSTER_KEY);
    if (raw == null) return migrateLegacyRoster();
    return sanitizeRoster(JSON.parse(raw));
  } catch {
    return emptyRoster();
  }
}

/**
 * Saved accounts with the active one first, then most recently used.
 * @returns {SavedAccount[]}
 */
export function listSavedAccounts() {
  const roster = getRoster();
  return [...roster.accounts].sort((a, b) => {
    if (a.key === roster.activeKey) return -1;
    if (b.key === roster.activeKey) return 1;
    return (b.lastUsed || 0) - (a.lastUsed || 0);
  });
}

/**
 * @returns {SavedAccount|null}
 */
export function getActiveAccount() {
  const roster = getRoster();
  return roster.accounts.find((row) => row.key === roster.activeKey) || null;
}

/**
 * Read the current session token.
 * @returns {string}
 */
export function getSessionToken() {
  return readItem(SESSION_TOKEN_KEY);
}

/**
 * Store or clear the session token without changing the roster.
 * Platform admin login uses this so it does not overwrite a saved person
 * or organization token.
 * @param {string} token Raw token from login, signup, or password change
 * @returns {void}
 */
export function setSessionToken(token) {
  writeItem(SESSION_TOKEN_KEY, token || "");
}

/**
 * Remove the active token and the saved account id.
 * Does not remove other accounts from the roster.
 * @returns {void}
 */
export function clearStoredSession() {
  writeItem(SESSION_TOKEN_KEY, "");
  writeItem(SESSION_USER_ID_KEY, "");
  writeItem(SESSION_USER_TYPE_KEY, "");
}

/**
 * Drop every saved account and the active token.
 * @returns {void}
 */
export function clearRoster() {
  writeRoster(emptyRoster());
  clearStoredSession();
}

/**
 * Put the active roster token back into the bearer-token slot.
 * Used after platform admin logout so the main site keeps its sessions.
 * @returns {boolean} True when a saved account was restored
 */
export function restoreActiveSessionToken() {
  const active = getActiveAccount();
  if (!active?.token) return false;
  writeActiveIdentity(active);
  return true;
}

/**
 * Replace the active account's token after a password change.
 * @param {string} token
 * @returns {void}
 */
export function replaceActiveToken(token) {
  if (!token) return;
  const roster = getRoster();
  const active = roster.accounts.find((row) => row.key === roster.activeKey);
  if (!active) {
    setSessionToken(token);
    return;
  }
  const next = { ...active, token, lastUsed: Date.now() };
  writeRoster({
    activeKey: next.key,
    accounts: roster.accounts.map((row) => (row.key === next.key ? next : row)),
  });
  writeActiveIdentity(next);
}

/**
 * Refresh the saved name and photo for an account already on the roster.
 * Does not change which account is active or replace its token.
 * @param {Object} account Profile payload
 * @returns {void}
 */
export function updateActiveProfile(account) {
  const type = accountTypeOf(account);
  const id = Number(account?.id);
  if ((type !== "user" && type !== "organization") || !id) return;
  const key = accountKey(type, id);
  const roster = getRoster();
  const existing = roster.accounts.find((row) => row.key === key);
  if (!existing) return;
  const next = {
    ...existing,
    name: accountDisplayName(account, type) || existing.name,
    username: account.username || existing.username,
    pfp_url: account.pfp_url || existing.pfp_url,
  };
  writeRoster({
    activeKey: roster.activeKey,
    accounts: roster.accounts.map((row) => (row.key === key ? next : row)),
  });
  if (roster.activeKey !== key) return;
  const bearer = readItem(SESSION_TOKEN_KEY);
  // Leave a platform-admin token in the bearer slot. The main site restores
  // this account's token when it mounts.
  if (!bearer || bearer === next.token) writeActiveIdentity(next);
}

/**
 * Save a person or organization session and make it the active account.
 * Signing in again to an account already on the roster refreshes its token.
 * @param {Object} account Account payload from login, signup, or profile setup
 * @param {string} token Raw session token
 * @returns {{ account?: SavedAccount, error?: string, atCapacity?: boolean }}
 */
export function rememberAccount(account, token) {
  const type = accountTypeOf(account);
  const id = Number(account?.id);
  if (type === "admin" || (type !== "user" && type !== "organization") || !id) {
    return { error: "Invalid account type" };
  }
  if (!token) return { error: "Assembly did not start a session." };

  const roster = getRoster();
  const key = accountKey(type, id);
  const existing = roster.accounts.find((row) => row.key === key);
  if (!existing && roster.accounts.length >= MAX_SAVED_ACCOUNTS) {
    return { error: ROSTER_FULL_MESSAGE, atCapacity: true };
  }

  const row = {
    key,
    id,
    type,
    token,
    name: accountDisplayName(account, type) || existing?.name || "",
    username: account.username || existing?.username || "",
    pfp_url: account.pfp_url || existing?.pfp_url || "",
    lastUsed: Date.now(),
  };
  const accounts = existing
    ? roster.accounts.map((item) => (item.key === key ? row : item))
    : [...roster.accounts, row];
  writeRoster({ activeKey: key, accounts });
  writeActiveIdentity(row);
  return { account: row };
}

/**
 * Make a saved account the one API calls act as.
 * @param {string} key
 * @param {{ commitToken?: boolean }} [options] When false, remember the account but leave the bearer token alone
 * @returns {SavedAccount|null}
 */
export function activateAccount(key, options = {}) {
  const commitToken = options.commitToken !== false;
  const roster = getRoster();
  const existing = roster.accounts.find((row) => row.key === key);
  if (!existing) return null;
  const next = { ...existing, lastUsed: Date.now() };
  writeRoster({
    activeKey: key,
    accounts: roster.accounts.map((row) => (row.key === key ? next : row)),
  });
  if (commitToken) writeActiveIdentity(next);
  return next;
}

/**
 * Remove one saved account. When it was active, the most recently used
 * remaining account becomes active.
 * @param {string} key
 * @param {{ commitToken?: boolean }} [options] When false, do not replace the bearer token
 * @returns {SavedAccount|null} The account that is active afterward
 */
export function removeAccount(key, options = {}) {
  const commitToken = options.commitToken !== false;
  const roster = getRoster();
  const accounts = roster.accounts.filter((row) => row.key !== key);
  if (roster.activeKey !== key) {
    const activeKey = accounts.some((row) => row.key === roster.activeKey) ? roster.activeKey : "";
    writeRoster({ activeKey, accounts });
    return accounts.find((row) => row.key === activeKey) || null;
  }
  const next = mostRecent(accounts);
  if (!next) {
    writeRoster(emptyRoster());
    if (commitToken) clearStoredSession();
    return null;
  }
  const touched = { ...next, lastUsed: Date.now() };
  writeRoster({
    activeKey: touched.key,
    accounts: accounts.map((row) => (row.key === touched.key ? touched : row)),
  });
  if (commitToken) writeActiveIdentity(touched);
  return touched;
}
